import { S25Util } from "../util/s25-util";
import { S25Const } from "../util/s25-const";
import { StandoutClassroomService } from "./standout.classroom.service";
import { UserprefService } from "./userpref.service";
import { FlsService } from "./fls.service";
import { SettingsObj, SettingsService } from "./settings.service";
import { LynxService } from "./lynx.service";
import { Fls, hasReadFls, hasEditFls } from "../pojo/Fls";
import { AdmitActionLinkComponent } from "../modules/admit/admit.action.link.component";
import { DynamicValue } from "../modules/dynamic-content/dynamic.content.component";
import { S25State, S25States } from "./state/state.map";

export class AppPathsService {
    public static hasCrmAccess(fls: Fls, groupId: number) {
        return S25Const.crmEnabled && (groupId === -1 || hasReadFls(fls?.CRM));
    }

    // Move baseApps to a static method
    public static getBaseApps(fls: Fls, groupId: number, settings: SettingsObj): AppPath[] {
        return [
            {
                name: "groups",
                url: S25Util.getURL("", "/groups"),
                sortOrder: 1,
                displayName: "Group Administration",
                condition: () => ["C", "F"].includes(fls.SECURITY),
            },
            {
                name: "import",
                url: S25Util.getURL("/import/", ""),
                sortOrder: 2,
                displayName: "Data Import Tool",
                condition: () => groupId === -1,
            },
            {
                name: "reports",
                url: S25Util.getURL("", "/reports"),
                sortOrder: 3,
                displayName: "25Live Reports",
                condition: () => "N" !== fls.REP_LIST,
            },
            {
                name: "outlook-ui",
                url: S25Util.getURL("", "/outlook-ui"),
                sortOrder: 8,
                displayName: "Outlook Sync Administration",
                condition: () => "F" === fls.CAL_OUTLOOK,
            },

            {
                name: "analytics",
                url: S25Util.getURL("/x25/", ""),
                sortOrder: 10.1,
                displayName: "X25 Analytics",
                condition: () => hasEditFls(fls.M25_EXPORT),
            },
            {
                name: "optimizer",
                url: S25Util.getURL("/x25/", "").concat("#/Optimizer"),
                sortOrder: 7,
                displayName: "Optimizer",
                condition: () => "F" === fls.RUNS25 && S25Util.toBool(settings.CAN_USE_OPTIMIZER),
            },
            {
                name: "crm",
                dynamicValue: {
                    component: AdmitActionLinkComponent,
                    inputs: {
                        preAction: "createInstance",
                    },
                },
                sortOrder: 11,
                displayName: "CRM",
                condition: () => AppPathsService.hasCrmAccess(fls, groupId),
            },
            {
                name: "soc",
                url: StandoutClassroomService.getChatLink(),
                sortOrder: 10.2,
                displayName: "StandOut Classroom",
                condition: () =>
                    S25Const.socEnabled &&
                    (groupId === -1 || hasReadFls(fls?.CHAT)) &&
                    !!StandoutClassroomService.getChatLink(),
            },
            {
                name: "housing",
                state: S25States.typed.housing,
                sortOrder: 12,
                displayName: "Housing",
                condition: () => {
                    return S25Const.housingEnabled && groupId === -1;
                },
            },
        ];
    }

    public static async getExternalApps(): Promise<AppPath[]> {
        const isLoggedIn = await UserprefService.getLoggedIn();
        if (!isLoggedIn) return [];

        const fls = await FlsService.getFls();
        const groupId = await UserprefService.getGroupId();
        const settings = await SettingsService.getSettings();
        const lynx = await (isLoggedIn && LynxService.getUserInstances());

        // Use the static method to get baseApps
        const baseApps = this.getBaseApps(fls, groupId, settings);

        // Add LYNX instances dynamically
        const lynxApps: AppPath[] = (lynx || []).map((inst: string) => ({
            name: "lynx".concat(lynx.length > 1 ? `-${inst}` : ""),
            url: LynxService.getLynxLink(inst).link,
            sortOrder: 5,
            displayName: "LYNX".concat(lynx.length > 1 ? ` (${inst})` : ""),
        }));

        // Filter apps based on their condition
        const filteredApps = baseApps.filter((app) => app.condition()).map(({ condition, ...app }) => app); // Remove the condition property

        return [...lynxApps, ...filteredApps].sort((a, b) => a.sortOrder - b.sortOrder);
    }
}

export type AppPath = {
    name: string;
    displayName: string;
    sortOrder: number;
    url?: string;
    dynamicValue?: DynamicValue;
    state?: S25State;
    condition?: () => boolean;
};
